import {
  FileText,
  Users,
  ChevronRight,
  Calendar,
  Clock,
  Trash2,
  UserPlus,
  Mail,
  Send,
  Loader2,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import type {
  WorkspaceDetails,
  ProjectWithCount,
  WorkspaceInvitation,
  Member,
} from './SuperPanel';

interface WorkspaceDetailViewProps {
  workspaceDetails: WorkspaceDetails;
  selectedProjectIds: Set<string>;
  toggleProjectSelection: (projectId: string) => void;
  onDeleteWorkspace: () => void;
  onDeleteProjects: () => void;
  onProjectClick: (project: ProjectWithCount) => void;
  onAddMember: () => void;
  onEditMember: (member: Member) => void;
  onResendInvitation: (invitation: WorkspaceInvitation) => void;
  onRevokeInvitation: (invitation: WorkspaceInvitation) => void;
  pendingInvitationActionId: string | null;
  formatDate: (dateString: string) => string;
}

export function WorkspaceDetailView({
  workspaceDetails,
  selectedProjectIds,
  toggleProjectSelection,
  onDeleteWorkspace,
  onDeleteProjects,
  onProjectClick,
  onAddMember,
  onEditMember,
  onResendInvitation,
  onRevokeInvitation,
  pendingInvitationActionId,
  formatDate,
}: WorkspaceDetailViewProps) {
  const invitations = workspaceDetails.invitations || [];

  return (
    <div>
      {/* Workspace Info */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold">{workspaceDetails.workspace.name}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {workspaceDetails.workspace.subdomain}.ingesta.app
              </p>
            </div>
            <div className="flex items-start gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">{formatDate(workspaceDetails.workspace.created_at)}</p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={onDeleteWorkspace}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Workspace
              </Button>
            </div>
          </div>

          {/* Members */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Members ({workspaceDetails.members.length})
              </h3>
              <Button size="sm" onClick={onAddMember}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add member
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {workspaceDetails.members.map((member) => (
                <button
                  key={member.user_id}
                  type="button"
                  onClick={() => onEditMember(member)}
                  className={`group inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm transition-colors ${
                    member.role === 'admin'
                      ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                      : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                  }`}
                  aria-label={`Modify ${member.email}`}
                >
                  <span>
                    {member.email} ({member.role})
                  </span>
                  <Pencil className="h-3 w-3 opacity-50 group-hover:opacity-100" />
                </button>
              ))}
            </div>
          </div>

          {/* Pending Invitations */}
          {invitations.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Pending invitations ({invitations.length})
              </h3>
              <div className="space-y-2">
                {invitations.map((invite) => {
                  const busy = pendingInvitationActionId === invite.id;
                  return (
                    <div
                      key={invite.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-md border bg-amber-50/50"
                    >
                      <Mail className="h-4 w-4 text-amber-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {invite.email}
                        </div>
                        <div className="text-xs text-gray-500">
                          {invite.role} · invited {formatDate(invite.invited_at)}
                          {invite.expires_at &&
                            ` · expires ${formatDate(invite.expires_at)}`}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => onResendInvitation(invite)}
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-1" />
                            Re-invite
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={busy}
                        onClick={() => onRevokeInvitation(invite)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Projects */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold">
          Projects ({workspaceDetails.projects.length})
        </h3>
        {selectedProjectIds.size > 0 && (
          <Button
            variant="destructive"
            size="sm"
            onClick={onDeleteProjects}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete {selectedProjectIds.size} Project{selectedProjectIds.size > 1 ? 's' : ''}
          </Button>
        )}
      </div>
      {workspaceDetails.projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            No projects in this workspace
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {workspaceDetails.projects.map((project) => (
            <Card
              key={project.id}
              className={`hover:shadow-md transition-shadow ${
                selectedProjectIds.has(project.id) ? 'ring-2 ring-red-300 bg-red-50' : ''
              }`}
            >
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <Checkbox
                    checked={selectedProjectIds.has(project.id)}
                    onCheckedChange={() => toggleProjectSelection(project.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => onProjectClick(project)}
                  >
                    <div className="flex items-center gap-3">
                      <h4 className="font-medium truncate">{project.name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        project.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : project.status === 'completed'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                    {project.description && (
                      <p className="text-sm text-gray-500 mt-1 truncate">
                        {project.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {project.document_count} documents
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Created {formatDate(project.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Updated {formatDate(project.updated_at)}
                      </span>
                    </div>
                  </div>
                  <ChevronRight
                    className="h-5 w-5 text-gray-400 shrink-0 cursor-pointer"
                    onClick={() => onProjectClick(project)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
